import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { authClient } from "@/services/auth";
import { supabase } from "@/integrations/supabase/client";
import { RESET_PATH, isInRecovery, subscribeRecovery } from "@/services/auth/recovery";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

export const ProtectedRoute = ({ children, requireOnboarding = true }: ProtectedRouteProps) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  // Is the person using this browser part-way through a password reset?
  //
  // "The person using this browser", not "this browser": a reset belongs to one account
  // (FR-013). Asking without saying who is signed in gets a yes for everybody, and on a
  // shared browser that traps the second person in a loop, because the reset page sends a
  // stranger's visit back to the app while a blind guard would send it here again. So the
  // answer is only ever worked out once the signed-in user is known, which is why it starts
  // as false and why `loading` still covers the very first render.
  const [recovering, setRecovering] = useState(false);
  // Who is signed in right now. A ref, not state, because it is only ever read to re-answer
  // the question above; nothing renders from it, and a re-render on every auth event would
  // re-run the subscription for no reason.
  const currentUserId = useRef<string | null>(null);

  const syncRecovery = (userId: string | null) => {
    currentUserId.current = userId;
    setRecovering(isInRecovery(userId));
  };

  // Begin/end of a reset elsewhere in the app. The notification says a reset changed, not
  // whose, so the answer is re-derived against whoever is signed in here.
  useEffect(() => subscribeRecovery(() => setRecovering(isInRecovery(currentUserId.current))), []);

  useEffect(() => {
    const checkAuth = async () => {
      const { session } = await authClient.getSession();

      syncRecovery(session?.user?.id ?? null);
      setAuthenticated(!!session);

      if (session && requireOnboarding) {
        // Check if onboarding is completed (still uses supabase for DB queries)
        const { data } = await supabase
          .from("user_preferences")
          .select("onboarding_completed")
          .eq("user_id", session.user.id)
          .maybeSingle();

        setOnboardingCompleted(data?.onboarding_completed ?? false);
      } else if (session && !requireOnboarding) {
        // Don't check onboarding for routes that don't require it
        setOnboardingCompleted(true);
      }

      setLoading(false);
    };

    checkAuth();

    const unsubscribe = authClient.onAuthStateChange((_event, session) => {
      syncRecovery(session?.user?.id ?? null);
      setAuthenticated(!!session);
      if (!session) {
        setOnboardingCompleted(null);
      }
    });

    return unsubscribe;
  }, [requireOnboarding]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
            <div className="absolute inset-0 animate-ping rounded-full h-12 w-12 border-2 border-primary/30"></div>
          </div>
          <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  // An unfinished password reset *belonging to this user* outranks every other reason to
  // redirect (FR-005, and FR-013 for the "belonging to this user" part).
  //
  // It comes before the sign-in check because the user DOES hold a session: the recovery
  // link minted one. What they have not got is a password, so sending them to the login
  // card would ask for the very thing they have forgotten. It comes before the onboarding
  // check for the same reason: setting up an account before choosing a password leaves
  // that account behind a password nobody knows.
  if (recovering) {
    return <Navigate to={RESET_PATH} replace />;
  }

  if (!authenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to onboarding if not completed and we require it
  if (requireOnboarding && onboardingCompleted === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};
